import { useState } from 'react';
import {
  Container,
  Header,
  Box,
  Button,
  Form,
  FormField,
  Input,
  SpaceBetween,
  Link,
  Alert,
  Grid,
} from '@cloudscape-design/components';
import { ViewLayout } from '../../../components/ViewLayout';
import { useMutation } from '@tanstack/react-query';
import { SMSConsentUpdate } from '../../../common/types';
import { updateSMSConsent } from '../../../common/api/consent';

export default function SMSConsentPage() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [consentStatus, setConsentStatus] = useState<null | 'optIn' | 'optOut'>(null);
  const [error, setError] = useState<string | null>(null);

  const updateConsent = useMutation({
    mutationFn: async (data: SMSConsentUpdate) => await updateSMSConsent(data),
    onSuccess: (data) => {
      if (data) {
        setConsentStatus(data.consent_status as 'optIn' | 'optOut' | null);
      }
    },
    onError: (error) => {
      setConsentStatus(null);
      setError(error.message);
    },
  });

  // This function is used when the user opts in
  const handleOptIn = () => {
    if (!phoneNumber.trim()) {
      setError('Please enter your phone number.');
      return;
    }
    setError(null);
    updateConsent.mutate({ phone_number: phoneNumber, consent_status: 'optIn' });
  };

  // This function is used when the user opts out
  const handleOptOut = () => {
    if (!phoneNumber.trim()) {
      setError('Please enter your phone number.');
      return;
    }
    setError(null);
    updateConsent.mutate({ phone_number: phoneNumber, consent_status: 'optOut' });
  };

  return (
    <ViewLayout
      breadcrumbs={[
        { text: 'Consent', href: '' },
        { text: 'SMS', href: '' },
      ]}
      navigationHide
      header={
        <Header description="Consent to receive SMS messages from Real Time Feedback" variant="h1">
          SMS Consent
        </Header>
      }
    >
      <Grid
        gridDefinition={[
          {
            colspan: {
              default: 12,
              l: 8,
              s: 6,
            },
          },
        ]}
      >
        <Container header={<Header variant="h2">SMS Messaging Consent</Header>}>
          <SpaceBetween size="l">
            {/* Introductory Text */}
            <Box variant="p">
              To comply with our messaging policies, please provide your consent below if you would
              like to receive SMS updates regarding marketing opportunities.
            </Box>

            {/* Consent Details */}
            <Box variant="p">
              <strong>Consent Details:</strong>
              <br />
              By opting in, you agree to receive personalized marketing text messages from Real Time
              Feedback at the mobile number provided. Message and data rates may apply. You can opt
              out at any time.
              <br />
              <br />
              For more information, please review our{' '}
              <Link href="/consent/sms/details" target="_blank">
                Consent Details
              </Link>
              .
            </Box>

            {/* Consent Form */}
            <Form>
              <SpaceBetween direction="vertical" size="l">
                <FormField
                  label="Phone Number"
                  description="Enter the mobile number where you would like to receive SMS messages."
                >
                  <Input
                    value={phoneNumber}
                    onChange={({ detail }) => setPhoneNumber(detail.value)}
                    placeholder="+1 234 567 8900"
                  />
                </FormField>

                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="primary"
                    onClick={handleOptIn}
                    loading={updateConsent.isPending && consentStatus !== 'optIn'}
                    disabled={consentStatus === 'optIn' || !phoneNumber.trim()}
                  >
                    Opt In
                  </Button>
                  <Button
                    variant="normal"
                    onClick={handleOptOut}
                    loading={updateConsent.isPending && consentStatus !== 'optOut'}
                    disabled={consentStatus === 'optOut' || !phoneNumber.trim()}
                  >
                    Opt Out
                  </Button>
                </SpaceBetween>
              </SpaceBetween>
            </Form>

            {/* Display Confirmation Message */}
            {consentStatus && !error && !updateConsent.isPending && (
              <Alert type="success">
                {consentStatus === 'optIn'
                  ? 'Thank you for opting in to SMS messaging!'
                  : 'You have opted out of receiving SMS messaging.'}
              </Alert>
            )}

            {error && (
              <Box margin={{ bottom: 's' }}>
                <Alert type="error">{error}</Alert>
              </Box>
            )}
          </SpaceBetween>
        </Container>
      </Grid>
    </ViewLayout>
  );
}
